import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Divider,
} from '@mui/material';
import { Save, Tune } from '@mui/icons-material';
import { apiFetch, apiUrl } from '../../../config/api';

const PointsSettingsTab = ({ token }) => {
  const [settings, setSettings] = useState([]);
  const [billing, setBilling] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const getHeaders = useCallback(() => {
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      'x-api-key': process.env.REACT_APP_API_KEY
    };
  }, [token]);

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);
      const [pointsRes, billingRes] = await Promise.all([
        apiFetch(apiUrl('/settings/points'), { headers: getHeaders() }),
        apiFetch(apiUrl('/settings/billing'), { headers: getHeaders() })
      ]);
      const pointsData = await pointsRes.json();
      const billingData = await billingRes.json();
      if (pointsData.success) {
        setSettings(pointsData.data || []);
      }
      if (billingData.success) {
        setBilling(billingData.data || []);
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to load settings' });
    } finally {
      setLoading(false);
    }
  }, [getHeaders]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleChange = (key, value) => {
    setSettings(prev => prev.map(s => s.key === key ? { ...s, value } : s));
  };

  const handleBillingChange = (key, value) => {
    setBilling(prev => prev.map(s => s.key === key ? { ...s, value } : s));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage({ type: '', text: '' });
      const billingKeys = ['MARKETING_FEE_LE', 'MARKETING_FEE_MIN_BILL_LE', 'MARKETING_FEE_MAX_LE'];
      const billingPayload = billing.map((s) => {
        if (!billingKeys.includes(s.key)) return s;
        const n = parseFloat(s.value);
        return { ...s, value: Number.isFinite(n) ? n : 0 };
      });
      const [pointsRes, billingRes] = await Promise.all([
        apiFetch(apiUrl('/settings/points'), {
          method: 'PUT',
          headers: getHeaders(),
          body: JSON.stringify({ settings })
        }),
        apiFetch(apiUrl('/settings/billing'), {
          method: 'PUT',
          headers: getHeaders(),
          body: JSON.stringify({ settings: billingPayload })
        })
      ]);
      const pointsData = await pointsRes.json();
      const billingData = await billingRes.json();
      if (pointsData.success && billingData.success) {
        setMessage({ type: 'success', text: 'Settings updated successfully' });
      } else {
        setMessage({ type: 'error', text: pointsData.error?.message || billingData.error?.message || 'Failed to update settings' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to update settings' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Tune sx={{ mr: 2, color: 'primary.main' }} />
        <Typography variant="h5" component="h2">Points Configuration</Typography>
      </Box>

      {message.text && (
        <Alert severity={message.type} sx={{ mb: 3 }}>
          {message.text}
        </Alert>
      )}

      <Card elevation={2} sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Redemption & Earning Settings</Typography>
          <Divider sx={{ mb: 3 }} />
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Points per LE"
                type="number"
                value={settings.find(s => s.key === 'POINTS_PER_LE')?.value || ''}
                onChange={(e) => handleChange('POINTS_PER_LE', parseFloat(e.target.value) || 0)}
                helperText={settings.find(s => s.key === 'POINTS_PER_LE')?.description}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Max Points Pay Percent"
                type="number"
                value={settings.find(s => s.key === 'MAX_POINTS_PAY_PERCENT')?.value || ''}
                onChange={(e) => handleChange('MAX_POINTS_PAY_PERCENT', parseFloat(e.target.value) || 0)}
                helperText={settings.find(s => s.key === 'MAX_POINTS_PAY_PERCENT')?.description}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Default Ledger Min LE"
                type="number"
                value={settings.find(s => s.key === 'DEFAULT_LEDGER_MIN_LE')?.value || ''}
                onChange={(e) => handleChange('DEFAULT_LEDGER_MIN_LE', parseFloat(e.target.value) || 0)}
                helperText={settings.find(s => s.key === 'DEFAULT_LEDGER_MIN_LE')?.description}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Points Validity (days)"
                type="number"
                inputProps={{ min: 1 }}
                value={settings.find(s => s.key === 'POINTS_VALIDITY_DAYS')?.value || ''}
                onChange={(e) => handleChange('POINTS_VALIDITY_DAYS', parseFloat(e.target.value) || 0)}
                helperText={settings.find(s => s.key === 'POINTS_VALIDITY_DAYS')?.description}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card elevation={2} sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Marketing Fee</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            A flat fee charged to the place per transaction, in LE. It is skipped when the bill is below the
            minimum, and capped at the maximum. A place can override any of these.
          </Typography>
          <Divider sx={{ mb: 3 }} />
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Marketing fee (LE)"
                type="number"
                inputProps={{ min: 0, step: '0.01' }}
                value={billing.find(s => s.key === 'MARKETING_FEE_LE')?.value ?? ''}
                onChange={(e) => handleBillingChange('MARKETING_FEE_LE', e.target.value)}
                helperText={billing.find(s => s.key === 'MARKETING_FEE_LE')?.description || 'Default flat fee charged to places per transaction, in LE. A place can override this.'}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Minimum bill (LE)"
                type="number"
                inputProps={{ min: 0, step: '0.01' }}
                value={billing.find(s => s.key === 'MARKETING_FEE_MIN_BILL_LE')?.value ?? ''}
                onChange={(e) => handleBillingChange('MARKETING_FEE_MIN_BILL_LE', e.target.value)}
                helperText={billing.find(s => s.key === 'MARKETING_FEE_MIN_BILL_LE')?.description || 'The fee is skipped when the bill is below this amount. 0 = charge on any bill.'}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Maximum fee (LE)"
                type="number"
                inputProps={{ min: 0, step: '0.01' }}
                value={billing.find(s => s.key === 'MARKETING_FEE_MAX_LE')?.value ?? ''}
                onChange={(e) => handleBillingChange('MARKETING_FEE_MAX_LE', e.target.value)}
                helperText={billing.find(s => s.key === 'MARKETING_FEE_MAX_LE')?.description || 'Cap on the fee. 0 = no cap.'}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={20} /> : <Save />}
          onClick={handleSave}
          disabled={saving}
          size="large"
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </Box>
    </Box>
  );
};

export default PointsSettingsTab;
